import { Label } from '@/components/ui/label'
import type { CustomField } from '@/lib/contacts/types'
import { membershipStatuses } from '@/pages/SegmentBuiltInConditionRows'
import {
  eventConditionPhrases,
  type EventConditionField,
  useSegmentEvents,
} from '@/pages/SegmentEventConditions'
import type { SegmentEvent } from '@/pages/SegmentEventPicker'
import { moneyOperators } from '@/pages/SegmentRangeConditionRows'
import type { SegmentCondition } from '@/pages/SegmentRuleFields'

interface Named {
  id: number
  name: string
}

export interface SegmentReferences {
  membershipLevels: Named[]
  groups: Named[]
  customFields: CustomField[]
  chapters: Array<Named & { archived?: boolean }>
  courses: Array<{ id: number; title: string; status?: string }>
  events: SegmentEvent[]
}

type Describer = (condition: SegmentCondition, references: SegmentReferences) => string

const anyOf = (operator: string) => (operator === 'none_of' ? 'is not any of' : 'is any of')
const list = (values: Array<string | number>) => values.join(', ')
const labelled = (values: Array<string | number>, labels: Record<string, string>) =>
  values.map((value) => labels[String(value)] ?? String(value))
const names = (values: Array<string | number>, items: Named[]) =>
  values.map((id) => items.find((item) => item.id === id)?.name ?? `#${id}`)
const humanize = (value: string) =>
  value.replace(/_/g, ' ').replace(/^./, (first) => first.toUpperCase())

const statusLabels = Object.fromEntries(membershipStatuses)
const contactTypeLabels = { person: 'Person', organization: 'Organization' }
const moneyLabels = Object.fromEntries(moneyOperators)
const booleanLabels = { autopay: 'Autopay enrollment', archived: 'Archived' }

const describers: Record<string, Describer> = {
  membership_status: ({ operator, values }) =>
    `Membership status ${anyOf(operator)} ${list(labelled(values, statusLabels))}`,
  engagement_band: ({ operator, values }) => `Engagement Band ${anyOf(operator)} ${list(values)}`,
  affiliation_role: ({ operator, values }) => `Affiliation role ${anyOf(operator)} ${list(values)}`,
  contact_type: ({ operator, values }) =>
    `Contact type ${anyOf(operator)} ${list(labelled(values, contactTypeLabels))}`,
  membership_level: ({ operator, values }, { membershipLevels }) =>
    `Membership Level ${anyOf(operator)} ${list(names(values, membershipLevels))}`,
  group_membership: ({ operator, values }, { groups }) =>
    `Group membership ${anyOf(operator)} ${list(names(values, groups))}`,
  chapter_membership: describeChapter,
  completed_course: describeCompletedCourse,
  autopay: ({ values }) => `${booleanLabels.autopay} is ${values[0]}`,
  archived: ({ values }) => `${booleanLabels.archived} is ${values[0]}`,
  open_balance: ({ operator, values }) =>
    `Open balance ${moneyLabels[operator] ?? humanize(operator).toLowerCase()} ${values[0]}`,
  overdue_balance: ({ operator, values }) =>
    `Overdue balance ${moneyLabels[operator] ?? humanize(operator).toLowerCase()} ${values[0]}`,
  renewal_due: ({ operator, values }) =>
    operator === 'in_next_days'
      ? `Renewal-due in the next ${values[0]} days`
      : `Renewal-due between ${values[0]} and ${values[1]}`,
  member_since: ({ values }) => `Member-since between ${values[0]} and ${values[1]}`,
  lapsed_on: ({ values }) => `Lapsed in the last ${values[0]} days`,
}

function describeChapter({ operator, values }: SegmentCondition, { chapters }: SegmentReferences) {
  if (operator === 'is')
    return values[0] === 'none' ? 'Chapter in no chapter' : 'Chapter in any chapter'
  const chapterNames = values.map((id) => {
    const chapter = chapters.find((item) => item.id === id)
    return chapter ? `${chapter.name}${chapter.archived ? ' (archived)' : ''}` : `#${id}`
  })
  return `Chapter ${anyOf(operator)} ${list(chapterNames)}`
}

function describeCompletedCourse({ values }: SegmentCondition, { courses }: SegmentReferences) {
  const course = courses.find((item) => item.id === values[0])
  const title = course
    ? `${course.title}${course.status === 'archived' ? ' (archived)' : ''}`
    : `#${values[0]}`
  const since = values[1] ? ` on or after ${values[1]}` : ''
  return `Completed Course ${title}${since}`
}

function describeCustomField(
  { field, operator, values }: SegmentCondition,
  { customFields }: SegmentReferences,
) {
  const id = Number(field.split(':')[1])
  const customField = customFields.find((item) => item.id === id)
  const label = customField
    ? `${customField.label}${customField.active ? '' : ' (deactivated)'}`
    : `Custom field #${id}`
  if (operator === 'between') return `${label} between ${values[0]} and ${values[1]}`
  if (operator === 'any_of' || operator === 'none_of')
    return `${label} ${anyOf(operator)} ${list(values)}`
  return `${label} ${operator} ${values[0] ?? ''}`
}

function describeEvent(
  { field, operator, values }: SegmentCondition,
  { events }: SegmentReferences,
) {
  const [positive, negative] = eventConditionPhrases[field as EventConditionField]
  const eventNames = names(values, events)
  const subject =
    eventNames.length > 1 ? `any of ${list(eventNames)}` : (eventNames[0] ?? 'no events')
  return `${operator === 'is_not' ? negative : positive} ${subject}`
}

function describeGeneric({ field, operator, values }: SegmentCondition) {
  return `${humanize(field)} ${humanize(operator).toLowerCase()} ${list(values)}`
}

/** One sentence per condition, in the words the editing rows use. */
export function describeSegmentCondition(
  condition: SegmentCondition,
  references: SegmentReferences,
): string {
  const describer =
    describers[condition.field] ??
    (condition.field.startsWith('custom_field:')
      ? describeCustomField
      : condition.field in eventConditionPhrases
        ? describeEvent
        : describeGeneric)
  return describer(condition, references)
}

/**
 * The Segment rule a View Only seat sees: every condition as a sentence, with
 * no control that could add, change or remove one (DESIGN.md: roles remove,
 * never disable). The page title already carries the Segment's name.
 */
export function SegmentRuleReadOnly({
  conditions,
  ...references
}: { conditions: SegmentCondition[] } & Omit<SegmentReferences, 'events'>) {
  const { events } = useSegmentEvents()
  return (
    <div className="flex max-w-2xl flex-col gap-2">
      <Label className="text-muted-foreground">Conditions</Label>
      {conditions.length === 0 ? (
        <p className="pl-11 text-body-sm text-muted-foreground">No conditions.</p>
      ) : (
        <ol className="space-y-2">
          {conditions.map((condition, index) => (
            <li key={`${condition.field}-${index}`} className="flex items-start gap-2">
              <span className="w-9 shrink-0 pt-2 text-right text-caption uppercase tracking-wide text-muted-foreground">
                {index === 0 ? 'if' : 'and'}
              </span>
              <p className="flex-1 rounded-md border border-input px-3 py-2 text-body-sm">
                {describeSegmentCondition(condition, { ...references, events })}
              </p>
            </li>
          ))}
        </ol>
      )}
    </div>
  )
}
